/**
 * ODBM Special Edition Quantity — button-style shortcuts that set the
 * add-to-cart quantity (1 / 5 / 10 / 25 …) instead of nudging the +/- pill.
 *
 * Whole-file ODBM module (basename `odbm-*`), loaded from
 * snippets/odbm-special-edition-quantity.liquid (only when the product has
 * quantities to show). Tested in tests/odbm-special-edition-quantity.test.js.
 *
 * Markup contract (rendered by that snippet):
 *   <div data-odbm-seq data-odbm-seq-form="product-form-{sectionId}">
 *     <span data-odbm-seq-selected>1</span>
 *     <input type="radio" data-odbm-seq-option value="10">
 *     <input type="hidden" name="quantity" data-odbm-seq-fallback>  (optional)
 *   </div>
 *
 * The buttons never post anything themselves — the radios carry no `form`
 * attribute and sit outside the product form. They write into the section's
 * quantity input, which is what the cart, the vendor <price-per-item> and
 * odbm-atc-price.js all already read. The `input`/`change` events dispatched
 * after the write are what drive those: the vendor <quantity-input> revalidates
 * its +/- buttons off `change`, and odbm-atc-price.js reprices the button off
 * either.
 *
 * The fallback hidden input is the target when the merchant configured neither
 * a Quantity selector block nor the inline quantity pill — without it the
 * buttons would look live and silently add 1.
 *
 * Events:
 *   - delegated change on [data-odbm-seq-option] — a button was picked;
 *   - delegated input/change on input[name="quantity"] — the shopper typed or
 *     used +/-, so the selection is re-derived (and cleared when the quantity
 *     is not one of the offered values);
 *   - document "variant:changed" (product-info.js) and the vendor
 *     quantityUpdate pub-sub (QuantityInput.setQuantityBoundries resets the
 *     value to min without firing `change`) — both re-derive the selection.
 */
(() => {
  if (window.OdbmSpecialEditionQuantityInitialized) {
    return;
  }
  window.OdbmSpecialEditionQuantityInitialized = true;

  const PICKER = "[data-odbm-seq]";
  const OPTION = "[data-odbm-seq-option]";
  const FALLBACK = "[data-odbm-seq-fallback]";

  function quantityInputFor(picker) {
    const formId = picker.dataset.odbmSeqForm;
    const shared = formId
      ? document.querySelector(
          `input[name="quantity"][form="${formId}"]:not(${FALLBACK})`
        )
      : null;
    return shared || picker.querySelector(FALLBACK);
  }

  function showSelected(picker, value) {
    const label = picker.querySelector("[data-odbm-seq-selected]");
    if (label) {
      label.textContent = value;
    }
  }

  function apply(picker, radio) {
    const input = quantityInputFor(picker);
    if (!input) {
      return;
    }
    input.value = radio.value;
    showSelected(picker, radio.value);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // The quantity is the source of truth, not the button that was clicked: a
  // typed 7 leaves every button unchecked rather than lying about the state.
  function sync(picker) {
    const input = quantityInputFor(picker);
    const quantity = Number.parseInt(input?.value, 10);
    const value = Number.isFinite(quantity) ? String(quantity) : "";
    for (const radio of picker.querySelectorAll(OPTION)) {
      radio.checked = radio.value === value;
    }
    showSelected(picker, value);
  }

  function syncAll() {
    for (const picker of document.querySelectorAll(PICKER)) {
      sync(picker);
    }
  }

  function onChange(event) {
    const target = event.target;
    if (!target?.matches) {
      return;
    }
    if (target.matches(OPTION)) {
      const picker = target.closest(PICKER);
      if (picker) {
        apply(picker, target);
      }
      return;
    }
    if (target.name === "quantity") {
      syncAll();
    }
  }

  document.addEventListener("change", onChange);
  document.addEventListener("input", (event) => {
    if (event.target?.name === "quantity") {
      syncAll();
    }
  });
  document.addEventListener("variant:changed", syncAll);

  // PUB_SUB_EVENTS is a global const in global.js, not a window property.
  if (
    typeof PUB_SUB_EVENTS !== "undefined" &&
    typeof window.FoxThemeEvents?.subscribe === "function"
  ) {
    window.FoxThemeEvents.subscribe(PUB_SUB_EVENTS.quantityUpdate, syncAll);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", syncAll);
  } else {
    syncAll();
  }
})();
